import * as React from "react";
import { forwardRef } from "react";
import { styled } from "@mui/material/styles";
import { Box, BoxProps } from "@mui/material";
import { FONT_SIZES, FONT_WEIGHTS } from "../../constants/styles/typography";

// ToggleButton Props 인터페이스
export interface ToggleButtonProps
  extends Omit<BoxProps, "onClick" | "onChange"> {
  /**
   * 버튼의 선택 상태
   */
  selected?: boolean;
  /**
   * 버튼이 비활성화되었는지 여부
   */
  disabled?: boolean;
  /**
   * 버튼 클릭 시 호출되는 콜백 함수
   */
  onChange?: (selected: boolean) => void;
  /**
   * 버튼의 크기
   */
  size?: "small" | "medium" | "large";
  /**
   * 버튼에 표시될 텍스트
   */
  label?: string;
  /**
   * 버튼에 표시될 아이콘 (ReactNode)
   */
  icon?: React.ReactNode;
  /**
   * 아이콘의 위치
   */
  iconPosition?: "start" | "end";
  /**
   * 버튼의 색상 테마
   */
  color?: "primary" | "secondary" | "default";
  /**
   * 전체 너비를 차지할지 여부
   */
  fullWidth?: boolean;
  /** 테스트 앵커. 루트로 전파된다(`...props`). 값 규약은 `src/test/testIds.ts` 참조. */
  "data-testid"?: string;
}

// Styled ToggleButton Component
const StyledToggleButton = styled(Box)<{
  selected: boolean;
  disabled: boolean;
  size: "small" | "medium" | "large";
  color: "primary" | "secondary" | "default";
  fullWidth: boolean;
}>(({ theme, selected, disabled, size, color, fullWidth }) => {
  const sizeConfig = {
    small: {
      height: "26px",
      padding: "0 8px",
      fontSize: FONT_SIZES.xs,
      fontWeight: FONT_WEIGHTS.medium,
      borderRadius: "4px",
      gap: "4px",
    },
    medium: {
      height: "28px",
      padding: "0 10px",
      fontSize: FONT_SIZES.sm,
      fontWeight: FONT_WEIGHTS.medium,
      borderRadius: "4px",
      gap: "4px",
    },
    large: {
      height: "30px",
      padding: "0 12px",
      fontSize: "15px",
      fontWeight: FONT_WEIGHTS.semiBold,
      borderRadius: "4px",
      gap: "6px",
    },
  };

  const config = sizeConfig[size];

  // 색상별 스타일
  const getColorStyles = () => {
    switch (color) {
      case "primary":
        return {
          selected: {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
            borderColor: theme.palette.primary.main,
          },
          unselected: {
            backgroundColor: "transparent",
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
          },
        };
      case "secondary":
        return {
          selected: {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
            borderColor: theme.palette.secondary.main,
          },
          unselected: {
            backgroundColor: "transparent",
            color: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main,
          },
        };
      default:
        return {
          selected: {
            backgroundColor: theme.palette.common.white,
            color: theme.palette.text.primary,
            borderColor: theme.palette.common.white,
            boxShadow:
              "0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.20)",
          },
          unselected: {
            backgroundColor: "transparent",
            color: theme.palette.text.secondary,
            borderColor: "transparent",
            opacity: 0.6,
          },
        };
    }
  };

  const colorStyles = getColorStyles();
  const currentStyles = selected
    ? colorStyles.selected
    : colorStyles.unselected;

  return {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    height: config.height,
    padding: config.padding,
    fontSize: config.fontSize,
    fontWeight: config.fontWeight,
    borderRadius: config.borderRadius,
    gap: config.gap,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "all 0.2s ease-in-out",
    userSelect: "none",
    whiteSpace: "nowrap",
    minWidth: "fit-content",
    width: fullWidth ? "100%" : "auto",
    border: `1px solid ${currentStyles.borderColor}`,

    // 기본 스타일
    backgroundColor: currentStyles.backgroundColor,
    color: currentStyles.color,
    opacity: (currentStyles as any).opacity || 1,
    boxShadow: (currentStyles as any).boxShadow || "none",

    // 비활성화 상태
    ...(disabled && {
      opacity: 0.4,
      cursor: "not-allowed",
    }),

    // 호버 상태 (비활성화되지 않은 경우)
    ...(!disabled && {
      "&:hover": {
        opacity: selected ? 1 : 0.8,
        transform: "translateY(-1px)",
        boxShadow: selected
          ? (currentStyles as any).boxShadow || "none"
          : "0 2px 4px rgba(0, 0, 0, 0.1)",
      },
    }),

    // 활성 상태 (클릭 시)
    ...(!disabled && {
      "&:active": {
        transform: "translateY(0)",
        boxShadow: selected
          ? (currentStyles as any).boxShadow || "none"
          : "0 1px 2px rgba(0, 0, 0, 0.1)",
      },
    }),

    // 포커스 상태
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: "2px",
    },
  };
});

// 아이콘 래퍼 스타일
const IconWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

// 텍스트 래퍼 스타일
const TextWrapper = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: 1,
  minWidth: 0,
}));

export const ToggleButton = forwardRef<HTMLDivElement, ToggleButtonProps>(
  (
    {
      selected = false,
      disabled = false,
      onChange,
      size = "medium",
      label,
      icon,
      iconPosition = "start",
      color = "default",
      fullWidth = false,
      ...props
    },
    ref
  ) => {
    const handleClick = () => {
      if (!disabled && onChange) {
        onChange(!selected);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    };

    const renderContent = () => {
      const iconElement = icon && <IconWrapper>{icon}</IconWrapper>;
      const textElement = label && <TextWrapper>{label}</TextWrapper>;

      if (iconPosition === "end") {
        return (
          <>
            {textElement}
            {iconElement}
          </>
        );
      }

      return (
        <>
          {iconElement}
          {textElement}
        </>
      );
    };

    return (
      <StyledToggleButton
        ref={ref}
        selected={selected}
        disabled={disabled}
        size={size}
        color={color}
        fullWidth={fullWidth}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-pressed={selected}
        aria-disabled={disabled}
        {...props}
      >
        {renderContent()}
      </StyledToggleButton>
    );
  }
);

ToggleButton.displayName = "ToggleButton";

export default ToggleButton;
